import { Box, Link, Typography, TypographyProps } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import ArticleMeta from '../../types/ArticleMeta';
import { ArticleCardVariant, getClampedTextStyles } from './CardUtils';
import ArticleCoverImage from '../Article/ArticleCoverImage';

interface MainContentAreaProps {
  readonly meta: ArticleMeta;
  readonly heading?: TypographyProps['component'];
  readonly hideDescription?: boolean;
  readonly hideCoverImage?: boolean;
  readonly showOnlyCustomCoverImage?: boolean;
  readonly variant: ArticleCardVariant;
  onTitleClick?: () => void;
}

export default function MainContentArea({
  meta,
  heading = 'h2',
  hideDescription = false,
  hideCoverImage = false,
  showOnlyCustomCoverImage = false,
  variant,
  onTitleClick = () => {},
}: MainContentAreaProps) {
  const showCoverImage = !(
    hideCoverImage ||
    (showOnlyCustomCoverImage && !meta.hasCoverImage())
  );

  return (
    <Link
      component={RouterLink}
      to={meta.getRoute()}
      title={meta.title}
      data-testid='article-card-link'
      onClick={() => onTitleClick()}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        justifyContent: 'space-between',
        textDecoration: 'none',
        color: 'text.primary',
        '&:hover': { color: 'primary.main' },
      }}
    >
      <Box>
        <Typography
          component={heading}
          variant='h3'
          sx={{
            marginBottom: hideDescription ? 0 : 1.5,
            ...getClampedTextStyles(variant === 'default' ? 2 : 3),
            hyphens: 'auto',
            textWrap: 'balance',
          }}
        >
          {meta.title}
        </Typography>
        {!hideDescription && (
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              ...getClampedTextStyles(2),
              hyphens: 'auto',
              textWrap: 'balance',
              whiteSpace: 'pre-wrap',
            }}
          >
            {meta.description}
          </Typography>
        )}
      </Box>
      {variant === 'mobile' && showCoverImage && (
        <Box sx={{ flexShrink: 0 }}>
          <ArticleCoverImage
            src={meta.coverImage}
            category={
              /* Only define the category if default cover image is shown. If 
              only custom cover image is shown, the category shoulod not be
              defined so that the default category image won't be shown as a
              fallback option. */
              showOnlyCustomCoverImage ? undefined : meta.categories.primary
            }
            height='60px'
          />
        </Box>
      )}
    </Link>
  );
}
